/**
 * Display helpers for one-off tournaments (admin#66).
 *
 * Presentation-only, same discipline as `coach-series.ts`: these turn fields
 * the tournament endpoints already return into strings/colors staff read. No
 * freeze, capacity, or conflict rule lives here — that is `prospin-api`'s
 * (api#155), and a client-side re-derivation of it is exactly what this repo's
 * brief for this feature forbids. Where a page uses a *hint* (e.g. "likely
 * frozen"), it is built from data the API already returned (the roster), and
 * an actual edit attempt still goes to the API for the real answer.
 */
import { PS } from './theme';
import type {
  AdminParticipant,
  ParticipantStatus,
  PaymentMode,
  TournamentLocale,
  TournamentStatus,
  TtarResultInputMode,
} from './types';

export const TOURNAMENT_LOCALES: readonly TournamentLocale[] = ['en', 'ru', 'ka'];

export const LOCALE_LABELS: Record<TournamentLocale, string> = {
  en: 'English',
  ru: 'Russian',
  ka: 'Georgian',
};

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  pay_later: 'Pay later (after registration)',
  upfront: 'Pay upfront (before the seat is confirmed)',
};

/** PATCH fields the API freezes once a participant has ever confirmed (api#155). */
export const FROZEN_AFTER_CONFIRMATION = [
  'date',
  'start_time',
  'end_time',
  'table_ids',
  'fee_minor',
  'payment_mode',
  'registration_closes_at',
] as const;

export function tournamentStatusLabel(status: TournamentStatus): string {
  return status[0].toUpperCase() + status.slice(1);
}

export function tournamentStatusColor(status: TournamentStatus): string {
  switch (status) {
    case 'published':
      return PS.success;
    case 'cancelled':
      return PS.danger;
    case 'completed':
      return PS.textSec;
    case 'draft':
    default:
      return PS.info;
  }
}

const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatus, string> = {
  pending_payment: 'Pending payment',
  confirmed: 'Confirmed',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
  removed: 'Removed',
};

/** No "refunded" entry exists here on purpose — see the module doc. */
export function participantStatusLabel(status: ParticipantStatus): string {
  return PARTICIPANT_STATUS_LABELS[status] ?? status;
}

export function participantStatusColor(status: ParticipantStatus): string {
  switch (status) {
    case 'confirmed':
      return PS.success;
    case 'pending_payment':
      return PS.pendingText;
    case 'withdrawn':
    case 'cancelled':
    case 'removed':
      return PS.textSec;
    default:
      return PS.textSec;
  }
}

/** Integer minor units (tetri) → whole-GEL number, for a `<input type="number">`. */
export function feeGelFromMinor(feeMinor: number): number {
  return feeMinor / 100;
}

/** Whole/decimal GEL (as typed by an admin) → integer minor units for the wire. */
export function feeMinorFromGel(gel: number): number {
  return Math.round(gel * 100);
}

/** A concise staff-facing representation of the optional TTAR rating window. */
export function formatRatingWindow(min: number | null, max: number | null): string {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `≥ ${min}`;
  if (max != null) return `≤ ${max}`;
  return 'Any';
}

/** Labels for the two TTAR-owned result-entry modes exposed by the API. */
export const TTAR_RESULT_INPUT_MODE_LABELS: Record<TtarResultInputMode, string> = {
  PlayersWithOrganizerConfirm: 'Players enter, organizer confirms',
  OrganizerOnly: 'Organizer only',
};

/** Staff-facing warmup summary; the API owns the time calculation. */
export function formatWarmup(warmupMinutes: number, warmupStartTime: string): string {
  return warmupMinutes === 0
    ? '0 min · no warmup'
    : `${warmupMinutes} min · tables blocked from ${warmupStartTime}`;
}

/**
 * Whether the roster shows any participant that is (or, at some point, was)
 * confirmed — a **hint** only. The API's freeze is one-way (`first_confirmed_at`
 * is never exposed to this client and stays set even after that participant is
 * later removed), so this can under-report a stale freeze; that is fine, because
 * the actual PATCH still goes to the API and its 409 `frozen_field` is what a
 * page must surface when this hint is wrong. Never treat this as the freeze
 * rule itself — only as a reason to disable a control up front.
 */
export function hasEverConfirmedParticipant(participants: AdminParticipant[]): boolean {
  return participants.some((p) => p.status === 'confirmed');
}

/** Table names for a set of blocked table ids, in the order given. */
export function tableNamesLabel(
  tableIds: number[],
  tables: { id: number; name: string }[],
): string {
  if (tableIds.length === 0) return '—';
  const byId = new Map(tables.map((t) => [t.id, t.name]));
  return tableIds.map((id) => byId.get(id) ?? `#${id}`).join(', ');
}
